
import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "@/contexts/auth";
import { useUserProfile } from "@/contexts/profile/ProfileContext";
import { useSubscription } from "@/hooks/subscription";
import { initiateCheckout } from "@/hooks/subscription/checkoutHelpers";
import CustomPricingTable from "@/components/pricing/CustomPricingTable";
import PricingHeader from "@/components/pricing/PricingHeader";
import PricingFAQ from "@/components/pricing/PricingFAQ";
import AdUnit from "@/components/ads/AdUnit";
import AfterIdle from "@/components/util/AfterIdle";
import useFeatureFlag from "@/hooks/useFeatureFlag";
import { FEATURE_FLAGS } from '@/config/featureFlags';

const isCheckoutTier = (value: string | null): value is 'whatsapp_tier' | 'premium_tier' => (
  value === 'whatsapp_tier' || value === 'premium_tier'
);

const PricingContent = () => {
  const { user, loading: authLoading } = useAuth();
  const { profile, isLoading: isLoadingProfile } = useUserProfile();
  const { data: subscriptionStatus } = useSubscription();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const attemptedCheckoutRef = useRef<string | null>(null);
  const currentTier = subscriptionStatus?.tier || 'free_tier';
  const adsEnabled = useFeatureFlag(FEATURE_FLAGS.ENABLE_ADS);
  const checkoutTier = useMemo(() => {
    const value = searchParams.get('checkout');
    return isCheckoutTier(value) ? value : null;
  }, [searchParams]);
  const checkoutEmail = user?.email?.trim() || profile?.email?.trim() || null;

  const redirectToEmailCapture = (tierCode: 'whatsapp_tier' | 'premium_tier') => {
    navigate(`/profile?tab=profile&requireEmail=true&redirect=${encodeURIComponent(`/pricing?checkout=${tierCode}`)}`);
  };

  useEffect(() => {
    if (!user || !checkoutTier || authLoading || isLoadingProfile) {
      return;
    }

    if (!checkoutEmail) {
      redirectToEmailCapture(checkoutTier);
      return;
    }

    const attemptKey = `${user.id}:${checkoutTier}`;
    if (attemptedCheckoutRef.current === attemptKey) {
      return;
    }

    attemptedCheckoutRef.current = attemptKey;
    void initiateCheckout(checkoutTier);
  }, [authLoading, checkoutEmail, checkoutTier, isLoadingProfile, navigate, user]);

  const handlePlanSelect = async (planId: string) => {
    if (planId !== 'whatsapp_tier' && planId !== 'premium_tier') {
      return;
    }

    if (!user) {
      navigate(`/auth?tab=signup&redirect=${encodeURIComponent(`/pricing?checkout=${planId}`)}`);
      return;
    }

    if (!checkoutEmail) {
      redirectToEmailCapture(planId);
      return;
    }

    await initiateCheckout(planId);
  };

  return (
    <div className="overflow-hidden bg-brand-mist">
      <PricingHeader />

      <section className="px-4 py-12 md:py-16">
        <div className="mx-auto max-w-6xl">
          <CustomPricingTable
            className="w-full"
            currentTier={currentTier}
            onPlanSelect={(planId) => void handlePlanSelect(planId)}
            selectedPlanId={checkoutTier ?? currentTier}
          />
        </div>

        {/* Ad Unit Below Pricing Table */}
        {adsEnabled && (
          <AfterIdle timeoutMs={1200}>
            <div className="mx-auto mb-16 mt-10 flex max-w-5xl justify-center">
              <AdUnit adSlot="1714990002" adFormat="LEADERBOARD" className="w-full" />
            </div>
          </AfterIdle>
        )}
      </section>

      <PricingFAQ />

      {/* Ad Unit Below FAQ */}
      {adsEnabled && (
        <AfterIdle timeoutMs={1200}>
          <div className="bg-brand-mist px-4 py-12 md:py-16">
            <div className="mx-auto flex max-w-3xl justify-center">
              <AdUnit adSlot="7965312393" adFormat="BANNER" className="w-full" />
            </div>
          </div>
        </AfterIdle>
      )}
    </div>
  );
};

export default PricingContent;
